import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetALL } from "../../../../../../../.globals/redux/actions";
import { clientQuery, clientMutation } from "../../../../../../../core/data/GraphQLApi";
import { ExceptionManager } from "../../../../../../../core/logManager";
import { endpoints } from 'Core/defaultValues';
import { clientGET } from 'Core/data/RestApi';

const INIT_STATE = {
    open: false,
    loading: false,
    asset: null,
    loadingAsset: null,
    assetId: null,
    error: null,
    result: null,

    loadingEntityLists: false,
    entityLists: [],

    loadingAssetTypeConfig: false,
    assetTypeEntityLists: [],

    loadingAssetSubtypes: false,
    assetSubtypes: [],
    deviceVin: null,
    loadingDecodeVin: false
};

const route = "modules/settings/views/asset/modals/createEditAsset/redux/slice";

const assetCreateEditSearchEntityLists = createAsyncThunk(
    "workOrders/asset/createEditAsset/searchEntityLists",
    async ({ limit = 100, offset = 0 }, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                assetCreateEditSearchEntityLists.query,
                {
                    conditions: [{
                        fields: ["entityName"],
                        terms: ["AssetType", "InspectionDeviceType", "COUNTRY", "STATE"]
                    }],
                    sort: {
                        field: "entityName",
                        order: "ASC"
                    },
                    limit,
                    offset,
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data || { items: [], total: 0 };
        } catch (exc) {
            ExceptionManager(exc, route, "searchEntityLists");
            return rejectWithValue(exc);
        }
    }
);

const assetTypeSearchEntityLists = createAsyncThunk(
    "workOrders/asset/assetTypeSearchEntityLists/searchEntityLists",
    async ({ limit = 100, offset = 0, label }, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                assetTypeSearchEntityLists.query,
                {
                    conditions: [
                        {
                            fields: ["entityName"],
                            terms: ["AssetType"],
                            exact: true
                            
                        },
                        {
                            fields: ["label"],
                            terms: [label],
                            exact: true
                        }
                    ],
                    sort: [],
                    limit,
                    offset,
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            const items = response?.data?.items?.map((item) => ({
                ...item,
                jsonValue: item.jsonValue ? JSON.parse(item.jsonValue) : null,
            })) || [];

            return { items, total: response?.data?.total || 0 };
        } catch (exc) {
            ExceptionManager(exc, route, "searchEntityLists");
            return rejectWithValue(exc);
        }
    }
);

const assetGetAsset = createAsyncThunk(
    "manage/asset/createEditAsset/getAsset",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                assetGetAsset.query,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data || null
        } catch (exc) {
            ExceptionManager(exc, route, "getAsset");
            return rejectWithValue(exc);
        }
    }
);

const assetCreateEditSearchAssetSubtypes = createAsyncThunk(
    "workOrders/asset/createEditAsset/searchAssetSubtypes",
    async ({ limit = 1000, offset = 0 }, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                assetCreateEditSearchAssetSubtypes.query,
                {
                    conditions: [],
                    sort: {
                        field: "name",
                        order: "ASC"
                    },
                    limit,
                    offset,
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data || { items: [], total: 0 };
        } catch (exc) {
            ExceptionManager(exc, route, "searchAssetSubtypes");
            return rejectWithValue(exc);
        }
    }
);

export const assetUpdateAsset = createAsyncThunk("manage/createEditAsset/updateAsset",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientMutation(
                `
                    mutation updateAsset (
                        $id: Int!
                        $label: String 
                        $unitId: Int
                        $assetTypeId: String
                        $assetSubtypeId: Int
                        $vin: String
                        $make: String
                        $model: String
                        $year: Int
                        $engine: Float
                        $mpgHighway: Int
                        $mpgCity: Int
                        $licensePlate: String
                        $registrationState: String
                        $registrationCountry: String
                        $registrationExpiration: String
                        $inspectionDeviceType: String
                        $inspectionTagId: String
                        $powerUnitNumber: String
                        $externalId: String
                        $integrationId: String
                        $tagIds: [Int]
                        $isActive: Boolean
                        $maintenanceCompany: String
                        $chassisSerialNumber: String
                        $engineSerialNumber: String
                    ) {
                        result: updateAsset (
                            id: $id
                            label: $label 
                            unitId: $unitId
                            assetTypeId: $assetTypeId
                            assetSubtypeId: $assetSubtypeId
                            vin: $vin
                            make: $make
                            model: $model
                            year: $year
                            engine: $engine
                            mpgHighway: $mpgHighway
                            mpgCity: $mpgCity
                            licensePlate: $licensePlate
                            registrationState: $registrationState
                            registrationCountry: $registrationCountry
                            registrationExpiration: $registrationExpiration
                            inspectionDeviceType: $inspectionDeviceType
                            inspectionTagId: $inspectionTagId
                            powerUnitNumber: $powerUnitNumber
                            externalId: $externalId
                            integrationId: $integrationId
                            tagIds: $tagIds
                            isActive: $isActive
                            maintenanceCompany: $maintenanceCompany
                            chassisSerialNumber: $chassisSerialNumber
                            engineSerialNumber: $engineSerialNumber
                        ) {
                            id
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result

        } catch (exc) {
            ExceptionManager(exc, route, "updateAsset");
            return rejectWithValue(exc);
        }
    }
);

export const assetCreateAsset = createAsyncThunk("manage/createEditAsset/createAsset",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientMutation(
                `
                    mutation createAsset (
                        $label: String!
                        $unitId: Int
                        $assetTypeId: String
                        $assetSubtypeId: Int
                        $vin: String
                        $make: String
                        $model: String
                        $year: Int
                        $engine: Float
                        $mpgHighway: Int
                        $mpgCity: Int
                        $licensePlate: String
                        $registrationState: String
                        $registrationCountry: String
                        $registrationExpiration: String
                        $inspectionDeviceType: String
                        $inspectionTagId: String
                        $powerUnitNumber: String
                        $externalId: String
                        $integrationId: String
                        $tagIds: [Int]
                        $isActive: Boolean
                        $maintenanceCompany: String
                        $chassisSerialNumber: String
                        $engineSerialNumber: String
                    ) {
                        result: createAsset (
                            label: $label 
                            unitId: $unitId
                            assetTypeId: $assetTypeId
                            assetSubtypeId: $assetSubtypeId
                            vin: $vin
                            make: $make
                            model: $model
                            year: $year
                            engine: $engine
                            mpgHighway: $mpgHighway
                            mpgCity: $mpgCity
                            licensePlate: $licensePlate
                            registrationState: $registrationState
                            registrationCountry: $registrationCountry
                            registrationExpiration: $registrationExpiration
                            inspectionDeviceType: $inspectionDeviceType
                            inspectionTagId: $inspectionTagId
                            powerUnitNumber: $powerUnitNumber
                            externalId: $externalId
                            integrationId: $integrationId
                            tagIds: $tagIds
                            isActive: $isActive
                            maintenanceCompany: $maintenanceCompany
                            chassisSerialNumber: $chassisSerialNumber
                            engineSerialNumber: $engineSerialNumber
                        ) {
                            id
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result

        } catch (exc) {
            ExceptionManager(exc, route, "createAsset");
            return rejectWithValue(exc);
        }
    }
);

export const assetDecodeVin = createAsyncThunk("manage/createEditAsset/decodeVin",
    async (payload, { rejectWithValue }) => {
        try {
            const { vin } = payload;
            const url = `${endpoints.NHTSA_URL}/api/vehicles/decodevinvaluesextended/${vin}?format=json`;

			const result = await clientGET('', {}, url, false);

			let asset = result?.Results?.[0] || {};

			if (!asset?.VIN) {
				asset = { error: true };
			}

			return asset;

        } catch (exc) {
            ExceptionManager(exc, route, "decodeVin");
            return rejectWithValue(exc);
        }
    }
);

assetCreateEditSearchEntityLists.query = `
    query ($conditions: [EntityListSearchInput], $sort: [EntityListSortInput], $limit: Int, $offset: Int) {
        data: searchEntityLists(
            conditions: $conditions
            sort: $sort
            limit: $limit
            offset: $offset
        ) {
            total
            items {
                entityId
                entityName
                parentId
                label
                value1
                value2
            }
        }
    }
`;

assetTypeSearchEntityLists.query = `
    query ($conditions: [EntityListSearchInput], $sort: [EntityListSortInput], $limit: Int, $offset: Int) {
        data: searchEntityLists(
            conditions: $conditions
            sort: $sort
            limit: $limit
            offset: $offset
        ) {
            total
            items {
                entityId
                entityName
                label
                jsonValue
              
            }
        }
    }
`;


assetGetAsset.query = `
    query ($id: Int!) {
        data: getAsset(
            id: $id
        ) {
            id
            label
            assetTypeId
            assetType {
              entityId
              label
            }
            assetSubtypeId
            assetSubtype {
              id
              name
            }
            vin
            make
            model
            year
            engine
            mpgHighway
            mpgCity
            licensePlate
            registrationState
            registrationCountry
            registrationExpiration
            inspectionDeviceType
            inspectionTagId
            powerUnitNumber
            externalId
            isActive
            chassisSerialNumber
            engineSerialNumber
            maintenanceCompany
            tags {
                id
                label
                color
            }
           
            
          
        }
    }
`;

assetCreateEditSearchAssetSubtypes.query = `
    query ($conditions: [AssetSubtypeSearchInput], $sort: [AssetSubtypeSortInput], $limit: Int, $offset: Int) {
        data: searchAssetSubtypes(
            conditions: $conditions
            sort: $sort
            limit: $limit
            offset: $offset
        ) {
            total
            items {
                id
                name
            }
        }
    }
`;

export { assetCreateEditSearchEntityLists, assetGetAsset, assetCreateEditSearchAssetSubtypes, assetTypeSearchEntityLists };

//Slice
const manageAssetCreateEditRedux = createSlice({
    name: "manage/asset/createEditAsset",
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
        openModalCreateEditAsset: (state, action) => {
            state.open = true;
            state.assetId = action.payload?.assetId || null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //assetCreateEditSearchEntityLists
        builder.addCase(assetCreateEditSearchEntityLists.pending, (state) => {
            state.loadingEntityLists = true;
        });
        builder.addCase(assetCreateEditSearchEntityLists.fulfilled, (state, action) => {
            state.loadingEntityLists = false;
            state.entityLists = action.payload.items;
        });
        builder.addCase(assetCreateEditSearchEntityLists.rejected, (state, action) => {
            state.loadingEntityLists = false;
            state.error = action.payload;
        });

          //assetTypeSearchEntityLists
        builder.addCase(assetTypeSearchEntityLists.pending, (state) => {
            state.loadingAssetTypeConfig = true;
        });
        builder.addCase(assetTypeSearchEntityLists.fulfilled, (state, action) => {
            state.loadingAssetTypeConfig = false;
            state.assetTypeEntityLists = action.payload.items;
        });
        builder.addCase(assetTypeSearchEntityLists.rejected, (state, action) => {
            state.loadingAssetTypeConfig = false;
            state.error = action.payload;
        });

        //assetGetAsset
        builder.addCase(assetGetAsset.pending, (state) => {
            state.loadingAsset = true;
        });
        builder.addCase(assetGetAsset.fulfilled, (state, action) => {
            state.loadingAsset = false;
            state.asset = action.payload;
        });
        builder.addCase(assetGetAsset.rejected, (state, action) => {
            state.loadingAsset = false;
            state.error = action.payload;
        });

        //assetCreateEditSearchAssetSubtypes
        builder.addCase(assetCreateEditSearchAssetSubtypes.pending, (state) => {
            state.loadingAssetSubtypes = true;
        });
        builder.addCase(assetCreateEditSearchAssetSubtypes.fulfilled, (state, action) => {
            state.loadingAssetSubtypes = false;
            state.assetSubtypes = action.payload.items;
        });
        builder.addCase(assetCreateEditSearchAssetSubtypes.rejected, (state, action) => {
            state.loadingAssetSubtypes = false;
            state.error = action.payload;
        });

        //assetUpdateAsset
        builder.addCase(assetUpdateAsset.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(assetUpdateAsset.fulfilled, (state, action) => {
            state.loading = false;
            state.result = action.payload;
        });
        builder.addCase(assetUpdateAsset.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //assetCreateAsset
        builder.addCase(assetCreateAsset.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(assetCreateAsset.fulfilled, (state, action) => {
            state.loading = false;
            state.result = action.payload;
        });
        builder.addCase(assetCreateAsset.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //assetDecodeVin
        builder.addCase(assetDecodeVin.pending, (state) => {
            state.loadingDecodeVin = true;
        });
        builder.addCase(assetDecodeVin.fulfilled, (state, action) => {
            state.loadingDecodeVin = false;
            state.deviceVin = action.payload;
        });
        builder.addCase(assetDecodeVin.rejected, (state, action) => {
            state.loadingDecodeVin = false;
            state.error = action.payload;
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = manageAssetCreateEditRedux;

// Extract and export each action creator by name
export const { openModalCreateEditAsset, reset } = actions;

// Export the reducer, either as a default or named export
export default reducer;
